import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  MessageSquare, Send, Clock, CheckCircle, AlertCircle, 
  ArrowLeft, Plus, ChevronRight, Headphones, Loader2,
  XCircle, RefreshCw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useToast } from '../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SupportPage = () => {
  const { toast } = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    subject: '',
    message: '',
    category: 'general',
    priority: 'normal'
  });
  
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    loadTickets();
  }, []);

  const loadTickets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/my-tickets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setTickets(data.tickets || []);
      }
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(newTicket)
      });
      
      if (response.ok) {
        toast({
          title: 'Ticket créé',
          description: 'Nous vous répondrons dans les plus brefs délais.'
        });
        setNewTicket({ subject: '', message: '', category: 'general', priority: 'normal' });
        setShowNewTicket(false);
        loadTickets();
      } else {
        throw new Error('Failed to create ticket');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de créer le ticket',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReply = async (ticketId) => {
    if (!replyMessage.trim()) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/support/${ticketId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: replyMessage })
      });
      
      if (response.ok) {
        toast({ title: 'Réponse envoyée' });
        setReplyMessage('');
        // Reload ticket details
        const detailRes = await fetch(`${API_URL}/api/support/${ticketId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (detailRes.ok) {
          const ticketData = await detailRes.json();
          setSelectedTicket(ticketData);
        }
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible d\'envoyer la réponse',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      open: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Ouvert' },
      in_progress: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'En cours' },
      resolved: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Résolu' },
      closed: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Fermé' }
    };
    const style = styles[status] || styles.open;
    return <Badge className={`${style.bg} ${style.text}`}>{style.label}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Basse' },
      normal: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Normale' },
      high: { bg: 'bg-orange-100', text: 'text-orange-600', label: 'Haute' },
      urgent: { bg: 'bg-red-100', text: 'text-red-600', label: 'Urgente' }
    };
    const style = styles[priority] || styles.normal;
    return <Badge variant="outline" className={`${style.bg} ${style.text} border-0`}>{style.label}</Badge>;
  };

  const categories = [
    { value: 'general', label: 'Question générale' },
    { value: 'technical', label: 'Problème technique' },
    { value: 'billing', label: 'Facturation / Paiement' },
    { value: 'feature', label: 'Demande de fonctionnalité' }
  ];

  return (
    <div className="space-y-6" data-testid="support-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/dashboard/smartfacture">
            <Button variant="ghost" size="sm" className="text-slate-500 hover:text-slate-700">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Headphones className="h-6 w-6 text-blue-600" />
              Support dédié
            </h1>
            <p className="text-slate-500 text-sm">Contactez notre équipe pour toute question</p>
          </div>
        </div>
        <Button 
          onClick={() => setShowNewTicket(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="new-ticket-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau ticket
        </Button>
      </div>

      {/* New Ticket Form */}
      {showNewTicket && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader>
            <CardTitle className="text-lg">Créer un ticket</CardTitle>
            <CardDescription>Décrivez votre problème ou question</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Sujet</label>
                <Input
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({...newTicket, subject: e.target.value})}
                  placeholder="Ex: Problème d'export PDF"
                  required
                  data-testid="ticket-subject"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie</label>
                  <select
                    value={newTicket.category}
                    onChange={(e) => setNewTicket({...newTicket, category: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="ticket-category"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Priorité</label>
                  <select
                    value={newTicket.priority}
                    onChange={(e) => setNewTicket({...newTicket, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                    data-testid="ticket-priority"
                  >
                    <option value="low">Basse</option>
                    <option value="normal">Normale</option>
                    <option value="high">Haute</option>
                    <option value="urgent">Urgente</option>
                  </select>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({...newTicket, message: e.target.value})}
                  placeholder="Décrivez votre problème en détail..."
                  rows={5}
                  required
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                  data-testid="ticket-message"
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => setShowNewTicket(false)}
                >
                  Annuler
                </Button>
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="submit-ticket-btn"
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Envoyer
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Tickets List or Detail */}
      {selectedTicket ? (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedTicket(null)}
                  className="mb-2 -ml-2"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Retour aux tickets
                </Button>
                <CardTitle className="text-lg">{selectedTicket.subject}</CardTitle>
                <div className="flex items-center gap-2 mt-2">
                  {getStatusBadge(selectedTicket.status)}
                  {getPriorityBadge(selectedTicket.priority)}
                  <span className="text-sm text-slate-500">
                    Créé le {new Date(selectedTicket.created_at).toLocaleDateString('fr-FR')}
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Original message */}
            <div className="p-4 bg-slate-50 rounded-xl">
              <p className="text-slate-700 whitespace-pre-wrap">{selectedTicket.message}</p>
            </div>
            
            {/* Responses */}
            {selectedTicket.responses?.map((response, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-xl ${
                  response.from === 'admin' 
                    ? 'bg-blue-50 border border-blue-100 ml-8' 
                    : 'bg-slate-50 mr-8'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Badge variant="outline" className={
                    response.from === 'admin' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100'
                  }>
                    {response.from === 'admin' ? 'Support SmartFacture' : 'Vous'}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    {new Date(response.created_at).toLocaleString('fr-FR')}
                  </span>
                </div>
                <p className="text-slate-700 whitespace-pre-wrap">{response.message}</p>
              </div>
            ))}
            
            {/* Reply form */}
            {selectedTicket.status !== 'closed' && (
              <div className="pt-4 border-t">
                <div className="flex gap-3">
                  <textarea
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Votre réponse..."
                    rows={3}
                    className="flex-1 px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                    data-testid="reply-message"
                  />
                  <Button 
                    onClick={() => handleReply(selectedTicket.id)}
                    disabled={submitting || !replyMessage.trim()}
                    className="bg-blue-600 hover:bg-blue-700 self-end"
                    data-testid="send-reply-btn"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Mes tickets</CardTitle>
            <CardDescription>Historique de vos demandes de support</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : tickets.length === 0 ? (
              <div className="text-center py-12">
                <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucun ticket pour le moment</p>
                <Button 
                  onClick={() => setShowNewTicket(true)}
                  className="mt-4"
                  variant="outline"
                >
                  Créer mon premier ticket
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {tickets.map(ticket => (
                  <div 
                    key={ticket.id}
                    onClick={() => setSelectedTicket(ticket)}
                    className="p-4 border border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50/30 cursor-pointer transition-all"
                    data-testid={`ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-medium text-slate-800">{ticket.subject}</h3>
                        <p className="text-sm text-slate-500 mt-1 line-clamp-1">{ticket.message}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {getStatusBadge(ticket.status)}
                          {getPriorityBadge(ticket.priority)}
                          <span className="text-xs text-slate-400">
                            {new Date(ticket.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="border-emerald-200 bg-emerald-50">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="p-2 rounded-lg bg-emerald-100">
              <Clock className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-emerald-800">Temps de réponse</h3>
              <p className="text-sm text-emerald-600 mt-1">
                En tant qu'utilisateur Expert, vos tickets sont traités en priorité. 
                Temps de réponse moyen : moins de 4 heures ouvrées.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SupportPage;
